import { useEffect, useState, useMemo } from "react";
import { fetchTemples, fetchDeities, adminAssignImage, adminUpdateTemple } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { Landmark, Search, ChevronDown, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const SOCIALS = ["facebook", "instagram", "youtube", "x"];

function TempleEditor({ t, deities, onSaved }) {
  const [ed, setEd] = useState(() => ({
    ...t,
    festivals: (t.festivals || []).join("\n"),
    highlights: (t.highlights || []).join("\n"),
    social: t.social || {},
    deities: t.deities || [],
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setEd((e) => ({ ...e, [k]: v }));
  const setSocial = (k, v) => setEd((e) => ({ ...e, social: { ...e.social, [k]: v } }));
  const toggleDeity = (slug) => setEd((e) => ({ ...e, deities: (e.deities || []).includes(slug) ? e.deities.filter((s) => s !== slug) : [...(e.deities || []), slug] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: ed.name, deva_name: ed.deva_name, deity: ed.deity, deities: ed.deities || [], location: ed.location, state: ed.state, region: ed.region, era: ed.era,
        description: ed.description, description_hi: ed.description_hi, significance: ed.significance, significance_hi: ed.significance_hi,
        timings: ed.timings, aarti_timings: ed.aarti_timings, darshan_info: ed.darshan_info, pooja_info: ed.pooja_info, pooja_info_hi: ed.pooja_info_hi,
        website: ed.website, booking_url: ed.booking_url,
        festivals: (ed.festivals || "").split("\n").map((s) => s.trim()).filter(Boolean),
        highlights: (ed.highlights || "").split("\n").map((s) => s.trim()).filter(Boolean),
        social: ed.social || {},
      };
      const out = await adminUpdateTemple(t.slug, payload);
      onSaved(out);
      toast("Temple details saved", { description: "Run Sync (Preview → Live) to publish." });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-4 border-t border-line pt-4" data-testid={`temple-editor-${t.slug}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-ink-3">Name (English)<input className={inp} value={ed.name || ""} onChange={(e) => set("name", e.target.value)} data-testid={`temple-name-${t.slug}`} /></label>
        <label className="text-xs text-ink-3">देवनागरी नाम<input className={`${inp} font-deva`} value={ed.deva_name || ""} onChange={(e) => set("deva_name", e.target.value)} /></label>
        <label className="text-xs text-ink-3">Deity (primary label)<input className={inp} value={ed.deity || ""} onChange={(e) => set("deity", e.target.value)} /></label>
        <label className="text-xs text-ink-3">Era<input className={inp} value={ed.era || ""} onChange={(e) => set("era", e.target.value)} /></label>
        <label className="text-xs text-ink-3">Location<input className={inp} value={ed.location || ""} onChange={(e) => set("location", e.target.value)} /></label>
        <label className="text-xs text-ink-3">State<input className={inp} value={ed.state || ""} onChange={(e) => set("state", e.target.value)} /></label>
      </div>

      {/* Map presiding deities — shown as cards on the temple page */}
      <div data-testid={`temple-deities-picker-${t.slug}`}>
        <p className="mb-2 text-xs text-ink-3">Presiding Deities <span className="text-ink-3/70">(tap to map — shown as cards on the temple page)</span></p>
        <div className="flex flex-wrap gap-2">
          {deities.length === 0 ? <span className="text-xs text-ink-3">Loading deities…</span> : deities.map((d) => {
            const on = (ed.deities || []).includes(d.slug);
            return (
              <button key={d.slug} type="button" onClick={() => toggleDeity(d.slug)} data-testid={`temple-deity-opt-${d.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${on ? "border-saffron bg-[var(--saffron)]/10 text-saffron" : "border-line text-ink-2 hover:border-[var(--ink)]"}`}>
                <span className="font-deva">{d.deva_name}</span> {d.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-ink-3">Description (English)<textarea rows={3} className={inp} value={ed.description || ""} onChange={(e) => set("description", e.target.value)} /></label>
        <label className="text-xs text-ink-3">विवरण (Hindi)<textarea rows={3} className={`${inp} font-deva`} value={ed.description_hi || ""} onChange={(e) => set("description_hi", e.target.value)} /></label>
        <label className="text-xs text-ink-3">Significance (English)<textarea rows={3} className={inp} value={ed.significance || ""} onChange={(e) => set("significance", e.target.value)} /></label>
        <label className="text-xs text-ink-3">महत्त्व (Hindi)<textarea rows={3} className={`${inp} font-deva`} value={ed.significance_hi || ""} onChange={(e) => set("significance_hi", e.target.value)} /></label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-ink-3">Darshan Timings<input className={inp} value={ed.timings || ""} onChange={(e) => set("timings", e.target.value)} placeholder="e.g. 5:00 AM – 9:00 PM" data-testid={`temple-timings-${t.slug}`} /></label>
        <label className="text-xs text-ink-3">Aarti Timings<textarea rows={2} className={inp} value={ed.aarti_timings || ""} onChange={(e) => set("aarti_timings", e.target.value)} placeholder="e.g. Mangala Aarti 5:00 AM · Shej Aarti 9:00 PM" data-testid={`temple-aarti-${t.slug}`} /></label>
        <label className="text-xs text-ink-3">Darshan Info<textarea rows={2} className={inp} value={ed.darshan_info || ""} onChange={(e) => set("darshan_info", e.target.value)} placeholder="Queue / VIP darshan, special entry, etc." /></label>
        <label className="text-xs text-ink-3">Pooja / Seva Details (English)<textarea rows={2} className={inp} value={ed.pooja_info || ""} onChange={(e) => set("pooja_info", e.target.value)} placeholder="Abhishekam, Archana, offerings…" /></label>
        <label className="text-xs text-ink-3">पूजा / सेवा विवरण (Hindi)<textarea rows={2} className={`${inp} font-deva`} value={ed.pooja_info_hi || ""} onChange={(e) => set("pooja_info_hi", e.target.value)} /></label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-ink-3">Official Website URL<input className={inp} value={ed.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" data-testid={`temple-website-${t.slug}`} /></label>
        <label className="text-xs text-ink-3">Booking / Seva URL<input className={inp} value={ed.booking_url || ""} onChange={(e) => set("booking_url", e.target.value)} placeholder="https://… (darshan/pooja booking)" data-testid={`temple-booking-${t.slug}`} /></label>
        <label className="text-xs text-ink-3">Major Festivals (one per line)<textarea rows={3} className={inp} value={ed.festivals || ""} onChange={(e) => set("festivals", e.target.value)} /></label>
        <label className="text-xs text-ink-3">Highlights (one per line)<textarea rows={3} className={inp} value={ed.highlights || ""} onChange={(e) => set("highlights", e.target.value)} /></label>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        {SOCIALS.map((s) => (
          <label key={s} className="text-xs text-ink-3 capitalize">{s}<input className={inp} value={(ed.social || {})[s] || ""} onChange={(e) => setSocial(s, e.target.value)} placeholder="https://…" /></label>
        ))}
      </div>

      <button onClick={save} disabled={busy} data-testid={`temple-save-${t.slug}`} className="inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save details
      </button>
    </div>
  );
}

export default function AdminTemples() {
  const [temples, setTemples] = useState([]);
  const [deities, setDeities] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetchTemples().then((d) => setTemples(Array.isArray(d) ? d : (d.items || []))).catch(() => {}).finally(() => setLoading(false));
    fetchDeities().then((d) => setDeities(Array.isArray(d) ? d : (d.items || []))).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return temples;
    return temples.filter((t) => `${t.name} ${t.deva_name} ${t.location} ${t.state} ${t.circuit}`.toLowerCase().includes(ql));
  }, [temples, q]);

  const assign = async (slug, url) => {
    setTemples((ts) => ts.map((t) => t.slug === slug ? { ...t, image: url, image_url: url } : t));
    try {
      await adminAssignImage({ collection: "temples", key: slug, image_url: url });
      toast("Temple image updated", { description: "Republish (Sync) to update the live site." });
    } catch (e) { toast.error("Update failed", { description: e.response?.data?.detail || e.message }); }
  };

  const onSaved = (out) => setTemples((ts) => ts.map((t) => t.slug === out.slug ? { ...t, ...out } : t));

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-temples">
      <div className="mb-2 flex items-center gap-2.5">
        <Landmark className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Temples</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-ink-3">
        Set each temple's photo and full details — description, significance, darshan &amp; aarti timings, pooja/seva details,
        official website and booking links, festivals, highlights and social profiles. Fields left blank simply won't show on the page.
        Changes save instantly; run Sync (Preview → Live) to publish.
      </p>

      <div className="mb-6 flex items-center gap-2 border-b-2 border-line pb-2 focus-within:border-saffron">
        <Search className="h-5 w-5 text-ink-3" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search temple, place or circuit…" data-testid="temple-search" className="w-full bg-transparent py-1 text-ink placeholder:text-ink-3 focus:outline-none" />
      </div>

      {loading ? (
        <p className="py-16 text-center text-ink-3">Loading temples…</p>
      ) : (
        <div className="space-y-4">
          {shown.map((t) => {
            const isOpen = open === t.slug;
            return (
              <div key={t.slug} className="border border-line bg-surface p-5" data-testid={`temple-row-${t.slug}`}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-deva text-lg text-ink deva-lh">{t.deva_name}</p>
                    <p className="text-sm text-ink-2">{t.name} <span className="text-ink-3">· {t.location}, {t.state} · {t.circuit}</span></p>
                  </div>
                  <button onClick={() => setOpen(isOpen ? null : t.slug)} data-testid={`temple-edit-toggle-${t.slug}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron">
                    {isOpen ? "Close" : "Edit details"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                <ImageUploader label="Temple photo" testid={`temple-img-${t.slug}`} value={t.image_url || t.image || ""} onChange={(v) => assign(t.slug, v)} />
                {isOpen && <TempleEditor t={t} deities={deities} onSaved={onSaved} />}
              </div>
            );
          })}
          {shown.length === 0 && <p className="py-10 text-center text-ink-3">No temples match “{q}”.</p>}
        </div>
      )}
    </div>
  );
}
